import type { AnyEntity, Folder, Mutation, Note, Project, Reminder, Task } from "@hq/contracts";
import { deleteFolder, moveFolder, renameFolder, reorderFolder, restoreFolder } from "./folders";
import {
  deleteNote,
  moveNote,
  renameNote,
  restoreNote,
  setNotePinned,
  setNoteTags,
  updateNoteContent,
  updateNoteMarkdown,
} from "./notes";
import {
  deleteProject,
  renameProject,
  setProjectArea,
  reorderProject,
  restoreProject,
  setProjectNotesFolder,
  setProjectStatus,
} from "./projects";
import {
  deleteReminder,
  dismissReminder,
  markReminderDelivered,
  rescheduleReminder,
  snoozeReminder,
} from "./reminders";
import {
  planTask,
  completeTask,
  deleteTask,
  moveTaskToProject,
  renameTask,
  reopenTask,
  reorderTask,
  restoreTask,
  scheduleTask,
  setTaskDescription,
  setTaskDescriptionContent,
  setTaskDueDate,
  setTaskEstimate,
  setTaskEvening,
  setTaskHeading,
  setTaskParent,
  setTaskPriority,
  setTaskStatus,
  setTaskTags,
} from "./tasks";
import type { NowContext } from "./time";

export class MutationTargetMissing extends Error {
  constructor(public readonly mutation: Mutation) {
    super(`Mutation ${mutation.type} targets a missing entity`);
  }
}

/**
 * The single reducer from a mutation to the entity it produces. Used by local services
 * (optimistic write), by the sync engine to rebase pending mutations on top of pulled rows,
 * and by the server (M2) to apply pushed mutations. `current` is the entity before the
 * mutation; it is undefined only for `*.create`.
 *
 * `ctx.now` becomes the entity's `updatedAt`. Callers pass the mutation envelope's
 * `createdAt` so replay is deterministic.
 */
export function applyMutation(
  current: AnyEntity | undefined,
  m: Mutation,
  ctx: NowContext,
): AnyEntity {
  switch (m.type) {
    case "tasks.create":
      return m.task;
    case "notes.create":
      return m.note;
    case "folders.create":
      return m.folder;
    case "areas.create":
      return m.area;
    case "projects.create":
      return m.project;
    case "reminders.create":
      return m.reminder;
  }
  if (!current) throw new MutationTargetMissing(m);
  switch (m.type) {
    case "tasks.rename":
      return renameTask(current as Task, m.title, ctx);
    case "tasks.setDescription":
      return setTaskDescription(current as Task, m.description, ctx);
    case "tasks.setDescriptionContent":
      return setTaskDescriptionContent(current as Task, m, ctx);
    case "tasks.complete":
      return { ...completeTask(current as Task, ctx), completedAt: m.completedAt };
    case "tasks.reopen":
      return reopenTask(current as Task, ctx);
    case "tasks.setStatus":
      return setTaskStatus(current as Task, m.status, ctx);
    case "tasks.setDueDate":
      return setTaskDueDate(current as Task, m.dueDate, ctx);
    case "tasks.plan":
      return planTask(current as Task, m.plannedDate, ctx);
    case "tasks.schedule":
      return scheduleTask(current as Task, m.scheduledAt, m.scheduledDurationMinutes, ctx);
    case "tasks.moveToProject":
      return moveTaskToProject(current as Task, m.projectId, ctx);
    case "tasks.setPriority":
      return setTaskPriority(current as Task, m.priority, ctx);
    case "tasks.setEvening":
      return setTaskEvening(current as Task, m.evening, ctx);
    case "tasks.setHeading":
      return setTaskHeading(current as Task, m.heading, ctx);
    case "tasks.setEstimate":
      return setTaskEstimate(current as Task, m.estimateMinutes, ctx);
    case "tasks.setParent":
      return setTaskParent(current as Task, m.parentTaskId, ctx);
    case "tasks.setTags":
      return setTaskTags(current as Task, m.tags, ctx);
    case "tasks.reorder":
      return reorderTask(current as Task, m.sortOrder, ctx);
    case "tasks.delete":
      return deleteTask(current as Task, ctx);
    case "tasks.restore":
      return restoreTask(current as Task, ctx);
    case "notes.updateMarkdown":
      return updateNoteMarkdown(current as Note, m.markdown, ctx);
    case "notes.updateContent":
      return updateNoteContent(current as Note, m, ctx);
    case "notes.rename":
      return renameNote(current as Note, m.title, ctx);
    case "notes.move":
      return moveNote(current as Note, m.folderId, ctx);
    case "notes.reorder":
      // Reordering navigation does not count as editing the note's content.
      return { ...(current as Note), sortOrder: m.sortOrder };
    case "notes.setPinned":
      return setNotePinned(current as Note, m.pinned, ctx);
    case "notes.setTags":
      return setNoteTags(current as Note, m.tags, ctx);
    case "notes.delete":
      return deleteNote(current as Note, ctx);
    case "notes.restore":
      return restoreNote(current as Note, ctx);
    case "folders.rename":
      return renameFolder(current as Folder, m.name, ctx);
    case "folders.move":
      // Cycle validation happens in services with the full folder list; here the move is applied as given.
      return moveFolder(current as Folder, m.parentId, [], ctx);
    case "folders.reorder":
      return reorderFolder(current as Folder, m.sortOrder, ctx);
    case "folders.delete":
      return deleteFolder(current as Folder, ctx);
    case "folders.restore":
      return restoreFolder(current as Folder, ctx);
    case "projects.rename":
      return renameProject(current as Project, m.name, ctx);
    case "projects.setArea":
      return setProjectArea(current as Project, m.area, ctx);
    case "projects.setStatus":
      return setProjectStatus(current as Project, m.status, ctx);
    case "projects.setNotesFolder":
      return setProjectNotesFolder(current as Project, m.notesFolderId, ctx);
    case "projects.reorder":
      return reorderProject(current as Project, m.sortOrder, ctx);
    case "projects.delete":
      return deleteProject(current as Project, ctx);
    case "projects.restore":
      return restoreProject(current as Project, ctx);
    case "reminders.reschedule":
      return rescheduleReminder(current as Reminder, m.triggerAt, ctx);
    case "reminders.snooze":
      return snoozeReminder(current as Reminder, m.snoozedUntil, ctx);
    case "reminders.dismiss":
      return dismissReminder(current as Reminder, ctx);
    case "reminders.markDelivered":
      return markReminderDelivered(current as Reminder, ctx);
    case "reminders.delete":
      return deleteReminder(current as Reminder, ctx);
  }
}

/** Mutations that replace free-form content and therefore carry `baseRevision` (Section 15). */
export function isRevisionGuarded(m: Mutation): m is Extract<Mutation, { baseRevision: number }> {
  return (
    m.type === "notes.updateMarkdown" ||
    m.type === "notes.updateContent" ||
    m.type === "notes.rename" ||
    m.type === "tasks.setDescription" ||
    m.type === "tasks.setDescriptionContent"
  );
}
